// exclusiveViewerCameraActions.js
'use strict';
import { MERGE_OPERATION_DATA } from '../ActionTypes.js';

import {
  ensureLocalTrack,
  ensureLocalTracks,
  publishLocalTracks,
  unpublishLocalTracks,
  getIsLocalPublishing,
} from '../resource/agoraLocalPublishing.js';
import {
  getLocalTracks,
  hasAnyLocalTrack,
  getLocalTrackEntry,
  releaseLocalTracks,
  setLocalTrackEnabled,
  switchLocalCamera,
  getHasMultipleCameras,
  LOCAL_TRACK_STEP,
  LOCAL_TRACK_TYPE,
  SWITCH_CAMERA_REASON,
} from '../resource/agoraLocalTracks.js';
import addModal from '../action/addModal.js';
import toggleModal from '../action/toggleModal.js';
import removeModals from '../action/removeModals.js';
import pushToastr from '../action/pushToastr.js';
import trackMixpanel from '../resource/mixpanel.js';
import { EventTypes } from '../resource/trackingConstants.js';
import getLiveStreamData from '../selector/getLiveStreamData.js';
import {
  getViewerCameraData,
  getViewerCameraSlicePath,
} from '../selector/getLivestreamComputedData.js';
import {
  ExclusivePrivateStatus,
  PermissionMode,
  VIEWER_CAMERA_START_MODAL_ID,
  VIEWER_CAMERA_MORE_MODAL_ID,
} from '../resource/liveStreamConstants.js';

const PREVIEW_TRIGGER = {
  AUTO: 'auto',
  START_MODAL: 'start_modal',
  TOGGLE: 'toggle',
  OFF_PANEL: 'off_panel',
};
const PUBLISH_STOP_REASON = {
  PERFORMING_END: 'performing_end',
  UNMOUNT: 'unmount',
};

const getChannelId = (state, streamId) =>
  getLiveStreamData(state, [streamId], 'sessionId');

const getExclusivePrivateStatus = (state, streamId) =>
  getLiveStreamData(state, [streamId, 'exclusivePrivate'], 'status');

const getIsExclusivePrivateActive = status =>
  status === ExclusivePrivateStatus.PREPARING ||
  status === ExclusivePrivateStatus.PERFORMING;

// Session facts every action reads before doing anything: the Agora channel
// and the 1v1 stage, plus the shared entry condition combining the two.
const getViewerCameraSessionContext = ({ state, streamId }) => {
  const channelId = getChannelId(state, streamId);
  const status = getExclusivePrivateStatus(state, streamId);
  return {
    channelId,
    status,
    isSessionActive: !!channelId && getIsExclusivePrivateActive(status),
  };
};

const getIsCurrentViewerCameraChannel = ({
  getState,
  streamId,
  channelId,
  requiredStatus,
}) => {
  const state = getState();
  const status = getExclusivePrivateStatus(state, streamId);
  return (
    getChannelId(state, streamId) === channelId &&
    (requiredStatus
      ? status === requiredStatus
      : getIsExclusivePrivateActive(status))
  );
};

const getIsCurrentViewerCameraEntry = ({
  getState,
  streamId,
  channelId,
  entry,
  requiredStatus,
}) =>
  getIsCurrentViewerCameraChannel({
    getState,
    streamId,
    channelId,
    requiredStatus,
  }) && getLocalTrackEntry({ channelId }) === entry;

// Holds the local-track entry an operation started with so each post-await
// re-check reads one place. The re-check positions themselves are race fixes
// — callers must keep calling isCurrent after every await, at the same spots.
const createViewerCameraEntryGuard = ({
  getState,
  streamId,
  channelId,
  entry,
}) => {
  let currentEntry = entry;
  return {
    getEntry: () => currentEntry,
    setEntry: entry => {
      currentEntry = entry;
    },
    refreshEntry: () => {
      currentEntry = getLocalTrackEntry({ channelId });
    },
    isCurrent: ({ requiredStatus } = {}) =>
      getIsCurrentViewerCameraEntry({
        getState,
        streamId,
        channelId,
        entry: currentEntry,
        requiredStatus,
      }),
  };
};

const TRACK_ENABLED_DATA_KEY = {
  [LOCAL_TRACK_TYPE.AUDIO]: 'isAudioEnabled',
  [LOCAL_TRACK_TYPE.VIDEO]: 'isVideoEnabled',
};

const CAMERA_RESET_DATA = {
  isPreviewReady: false,
  isPublishing: false,
  isAudioEnabled: true,
  isVideoEnabled: true,
  cameraSwitchVersion: 0,
  isInMainWindow: false,
  canSwitchCamera: true,
  hasMediaAccessError: false,
};

const SET_UP_FAILED_TOAST = { textKey: 'set_up_failed', color: 'error' };

// Same modal (and open shape) the exclusive camera preview flow uses.
const CAMERA_MICROPHONE_ASKER_MODAL_ID = 'CameraMicrophoneAsker';

const PERMISSION_DENIED_ERROR_CODE = 'PERMISSION_DENIED';

const getIsPermissionDeniedError = error =>
  error?.code === PERMISSION_DENIED_ERROR_CODE;

const TRACK_PERMISSION_MODE = {
  [LOCAL_TRACK_TYPE.AUDIO]: PermissionMode.MICROPHONE,
  [LOCAL_TRACK_TYPE.VIDEO]: PermissionMode.CAMERA,
};

// Merge per-track capture failures into one asker mode; null = nothing denied.
const getDeniedPermissionMode = ({ failures = [] } = {}) => {
  const isCameraDenied = failures.some(
    ({ trackType, error }) =>
      trackType === LOCAL_TRACK_TYPE.VIDEO && getIsPermissionDeniedError(error)
  );
  const isMicrophoneDenied = failures.some(
    ({ trackType, error }) =>
      trackType === LOCAL_TRACK_TYPE.AUDIO && getIsPermissionDeniedError(error)
  );
  if (isCameraDenied && isMicrophoneDenied) return PermissionMode.BOTH;
  if (isCameraDenied) return PermissionMode.CAMERA;
  if (isMicrophoneDenied) return PermissionMode.MICROPHONE;
  return null;
};

const openCameraMicrophoneAsker = ({ mode }) =>
  addModal({
    id: CAMERA_MICROPHONE_ASKER_MODAL_ID,
    isHigherThanAll: true,
    payload: { mode },
  });

const mergeViewerCameraData = ({ streamId, data }) => ({
  type: MERGE_OPERATION_DATA,
  payload: {
    selectPath: getViewerCameraSlicePath({ streamId }),
    data,
  },
});

/**
 * Mark the viewer camera preview stale so existing effects recapture tracks.
 * @kind action
 * @param {object} params
 * @param {string} params.streamId - Livestream id.
 * @returns {Function} Redux thunk action.
 * @example
 * dispatch(invalidateViewerCameraPreview({ streamId: 'stream-id' }));
 */
const invalidateViewerCameraPreview =
  ({ streamId }) =>
  dispatch =>
    dispatch(
      mergeViewerCameraData({
        streamId,
        data: { isPreviewReady: false, isPublishing: false },
      })
    );

/**
 * Open the viewer camera start modal.
 * @kind action
 * @returns {object} Redux action for opening the modal.
 * @example
 * dispatch(openViewerCameraStartModal());
 */
const openViewerCameraStartModal = () =>
  toggleModal({ modalId: VIEWER_CAMERA_START_MODAL_ID, isOpen: true });

/**
 * Open the viewer camera more menu.
 * @kind action
 * @returns {object} Redux action for opening the modal.
 * @example
 * dispatch(openViewerCameraMoreModal());
 */
const openViewerCameraMoreModal = () =>
  toggleModal({ modalId: VIEWER_CAMERA_MORE_MODAL_ID, isOpen: true });

const getCameraErrorData = error => ({
  step: error?.viewerCameraStep,
  errorName: error?.name,
  errorMessage: error?.message,
});

// Every viewer camera event carries the stream id and a client timestamp.
const trackViewerCameraEvent = ({ type, streamId, payload }) =>
  trackMixpanel({
    type,
    payload: { streamId, clientTimestamp: Date.now(), ...payload },
  });

// Restore saved mute settings after local tracks are created.
const restoreViewerCameraPreferences = async ({
  streamId,
  channelId,
  guard,
  getState,
}) => {
  const isCurrent = () => guard.isCurrent();
  if (!isCurrent()) return null;

  const localTracks = getLocalTracks({ channelId });
  const trackEnabledStates = {};
  for (const [trackType, dataKey] of Object.entries(TRACK_ENABLED_DATA_KEY)) {
    const track = localTracks?.[`${trackType}Track`];
    if (!track) {
      trackEnabledStates[dataKey] = false;
      continue;
    }
    let isEnabled = getViewerCameraData(getState(), streamId, dataKey);
    if (!isEnabled) {
      const isApplied = await setLocalTrackEnabled({
        channelId,
        trackType,
        isEnabled: false,
      }).catch(() => false);
      isEnabled = !isApplied;
    }
    if (!isCurrent()) return null;
    trackEnabledStates[dataKey] = isEnabled;
  }
  const canSwitchCamera = localTracks?.videoTrack
    ? await getHasMultipleCameras()
    : false;
  if (!isCurrent()) return null;
  return { ...trackEnabledStates, canSwitchCamera };
};

/**
 * Prepare local tracks for self-preview without publishing.
 * @kind action
 * @param {object} params
 * @param {string} params.streamId - Livestream id.
 * @param {string} [params.trigger] - Source that started the preview.
 * @returns {Function} Redux thunk action.
 * @example
 * dispatch(startViewerCameraPreview({ streamId: 'stream-id' }));
 */
const startViewerCameraPreview =
  ({ streamId, trigger = PREVIEW_TRIGGER.AUTO }) =>
  async (dispatch, getState) => {
    const { channelId, isSessionActive } = getViewerCameraSessionContext({
      state: getState(),
      streamId,
    });
    if (!isSessionActive) {
      return dispatch({ type: '' });
    }
    const guard = createViewerCameraEntryGuard({
      getState,
      streamId,
      channelId,
    });
    try {
      const captureResult = await ensureLocalTracks({ channelId });
      if (!captureResult) {
        return dispatch({ type: '' });
      }
      guard.setEntry(captureResult.entry);
      if (!guard.isCurrent()) {
        return dispatch({ type: '' });
      }
      const { failures } = captureResult;
      failures.forEach(({ trackType, error }) => {
        trackViewerCameraEvent({
          type: EventTypes.LIVESTREAM_VIEWER_CAMERA_PREVIEW_FAILED,
          streamId,
          payload: {
            trigger,
            trackType,
            isPartial: true,
            ...getCameraErrorData(error),
            step: LOCAL_TRACK_STEP.GET_USER_MEDIA,
          },
        });
      });
      const partialDeniedMode = getDeniedPermissionMode({ failures });
      if (partialDeniedMode) {
        dispatch(openCameraMicrophoneAsker({ mode: partialDeniedMode }));
      }
      const preferences = await restoreViewerCameraPreferences({
        streamId,
        channelId,
        guard,
        getState,
      });
      if (
        !preferences ||
        !hasAnyLocalTrack({ channelId }) ||
        !guard.isCurrent()
      ) {
        return dispatch({ type: '' });
      }
      return dispatch(
        mergeViewerCameraData({
          streamId,
          data: {
            isPreviewReady: true,
            ...preferences,
            hasMediaAccessError: false,
          },
        })
      );
    } catch (error) {
      if (error?.viewerCameraTrackEntry) {
        guard.setEntry(error.viewerCameraTrackEntry);
      }
      // Before any capture there is no track entry to compare, so fall
      // back to the channel-level check.
      const isCurrent = guard.getEntry()
        ? guard.isCurrent()
        : getIsCurrentViewerCameraChannel({
            getState,
            streamId,
            channelId,
          });
      if (!isCurrent) {
        return dispatch({ type: '' });
      }
      trackViewerCameraEvent({
        type: EventTypes.LIVESTREAM_VIEWER_CAMERA_PREVIEW_FAILED,
        streamId,
        payload: {
          trigger,
          isPartial: false,
          ...getCameraErrorData(error),
        },
      });
      // Ignore a stale failure after another preview succeeded.
      if (hasAnyLocalTrack({ channelId })) {
        return dispatch({ type: '' });
      }
      // A total failure carries every track's failure; fall back to the
      // single error (ensureLocalTrack tags its track type on it).
      const deniedMode = getDeniedPermissionMode({
        failures: error?.viewerCameraCaptureFailures ?? [
          { trackType: error?.viewerCameraTrackType, error },
        ],
      });
      if (deniedMode) {
        dispatch(openCameraMicrophoneAsker({ mode: deniedMode }));
      }
      return dispatch(
        mergeViewerCameraData({
          streamId,
          data: {
            isPreviewReady: false,
            hasMediaAccessError:
              error?.viewerCameraStep === LOCAL_TRACK_STEP.GET_USER_MEDIA,
          },
        })
      );
    }
  };

/**
 * Publish prepared tracks after the 1v1 starts performing.
 * @kind action
 * @param {object} params
 * @param {string} params.streamId - Livestream id.
 * @returns {Function} Redux thunk action.
 * @example
 * dispatch(publishViewerCamera({ streamId: 'stream-id' }));
 */
const publishViewerCamera =
  ({ streamId }) =>
  async (dispatch, getState) => {
    const { channelId, status } = getViewerCameraSessionContext({
      state: getState(),
      streamId,
    });
    const guard = createViewerCameraEntryGuard({
      getState,
      streamId,
      channelId,
      entry: getLocalTrackEntry({ channelId }),
    });
    if (
      !channelId ||
      status !== ExclusivePrivateStatus.PERFORMING ||
      !hasAnyLocalTrack({ channelId })
    ) {
      return dispatch({ type: '' });
    }

    const attemptAt = Date.now();
    trackViewerCameraEvent({
      type: EventTypes.LIVESTREAM_VIEWER_CAMERA_PUBLISH_ATTEMPT,
      streamId,
    });
    try {
      const localTracks = getLocalTracks({ channelId });
      const publishedTracks = await publishLocalTracks({
        channelId,
        localTracks,
      });
      if (
        !publishedTracks ||
        !getIsLocalPublishing({ channelId }) ||
        !guard.isCurrent({
          requiredStatus: ExclusivePrivateStatus.PERFORMING,
        })
      ) {
        return dispatch({ type: '' });
      }
      trackViewerCameraEvent({
        type: EventTypes.LIVESTREAM_VIEWER_CAMERA_PUBLISH_SUCCEEDED,
        streamId,
        payload: {
          msFromAttempt: Date.now() - attemptAt,
          hasVideo: !!publishedTracks.videoTrack,
          hasAudio: !!publishedTracks.audioTrack,
          channelId,
        },
      });
      return dispatch(
        mergeViewerCameraData({
          streamId,
          data: { isPublishing: true },
        })
      );
    } catch (error) {
      if (
        !guard.isCurrent({
          requiredStatus: ExclusivePrivateStatus.PERFORMING,
        })
      ) {
        return dispatch({ type: '' });
      }
      trackViewerCameraEvent({
        type: EventTypes.LIVESTREAM_VIEWER_CAMERA_PUBLISH_FAILED,
        streamId,
        payload: {
          ...getCameraErrorData(error),
          msFromAttempt: Date.now() - attemptAt,
        },
      });
      // Ignore a stale failure after another publish succeeded.
      if (
        getIsLocalPublishing({ channelId }) ||
        !hasAnyLocalTrack({ channelId })
      ) {
        return dispatch({ type: '' });
      }
      dispatch(
        mergeViewerCameraData({
          streamId,
          data: { isPublishing: false },
        })
      );
      dispatch(pushToastr(SET_UP_FAILED_TOAST));
      return dispatch(openViewerCameraStartModal());
    }
  };

/**
 * Capture missing tracks, or publish prepared tracks during performing.
 * @kind action
 * @param {object} params
 * @param {string} params.streamId - Livestream id.
 * @param {string} [params.trigger] - Source that started the retry.
 * @returns {Function} Redux thunk action.
 * @example
 * dispatch(startViewerCamera({ streamId: 'stream-id' }));
 */
const startViewerCamera =
  ({ streamId, trigger = PREVIEW_TRIGGER.START_MODAL }) =>
  (dispatch, getState) => {
    const { channelId, status } = getViewerCameraSessionContext({
      state: getState(),
      streamId,
    });
    if (
      channelId &&
      status === ExclusivePrivateStatus.PERFORMING &&
      hasAnyLocalTrack({ channelId })
    ) {
      return dispatch(publishViewerCamera({ streamId }));
    }
    return dispatch(startViewerCameraPreview({ streamId, trigger }));
  };

/**
 * Stop publishing and release the viewer's local tracks.
 * @kind action
 * @param {object} params
 * @param {string} params.streamId - Livestream id.
 * @returns {Function} Redux thunk action.
 * @example
 * dispatch(stopViewerCamera({ streamId: 'stream-id' }));
 */
const stopViewerCamera =
  ({ streamId }) =>
  async (dispatch, getState) => {
    const { channelId, status } = getViewerCameraSessionContext({
      state: getState(),
      streamId,
    });
    const isExclusivePrivateActive = getIsExclusivePrivateActive(status);

    dispatch(
      removeModals({
        ids: [VIEWER_CAMERA_START_MODAL_ID, VIEWER_CAMERA_MORE_MODAL_ID],
      })
    );
    const resetAction = dispatch(
      mergeViewerCameraData({
        streamId,
        data: CAMERA_RESET_DATA,
      })
    );

    let publishedMs;
    if (channelId) {
      // Snapshot ownership before unpublish yields.
      const entry = getLocalTrackEntry({ channelId });
      try {
        ({ publishedMs } = await unpublishLocalTracks({ channelId }));
      } finally {
        releaseLocalTracks({ channelId, entry });
      }
    }

    const reason = isExclusivePrivateActive
      ? PUBLISH_STOP_REASON.UNMOUNT
      : PUBLISH_STOP_REASON.PERFORMING_END;

    // Do not report teardown when tracks were never published.
    if (publishedMs !== undefined) {
      trackViewerCameraEvent({
        type: EventTypes.LIVESTREAM_VIEWER_CAMERA_PUBLISH_STOPPED,
        streamId,
        payload: {
          reason,
          publishedMs,
        },
      });
    }
    return resetAction;
  };

/**
 * Enable or disable a local viewer camera track. Before the 1v1 is active
 * (pre-invite camera preview), only the mute preference is recorded — the
 * preview hook owns the local tracks, and restoreViewerCameraPreferences
 * applies the preference when the 1v1 starts.
 * @kind action
 * @param {object} params
 * @param {string} params.streamId - Livestream id.
 * @param {'audio'|'video'} params.trackType - Local track type.
 * @param {boolean} params.isEnabled - Whether the track is enabled.
 * @returns {Function} Redux thunk action.
 * @example
 * dispatch(setViewerCameraTrackEnabled({
 *   streamId: 'stream-id',
 *   trackType: 'video',
 *   isEnabled: false,
 * }));
 */
const setViewerCameraTrackEnabled =
  ({ streamId, trackType, isEnabled }) =>
  async (dispatch, getState) => {
    const { channelId, isSessionActive } = getViewerCameraSessionContext({
      state: getState(),
      streamId,
    });
    if (!channelId) {
      return dispatch({ type: '' });
    }
    if (!isSessionActive) {
      return dispatch(
        mergeViewerCameraData({
          streamId,
          data: { [TRACK_ENABLED_DATA_KEY[trackType]]: isEnabled },
        })
      );
    }
    const guard = createViewerCameraEntryGuard({
      getState,
      streamId,
      channelId,
      entry: getLocalTrackEntry({ channelId }),
    });
    let didCreateTrack = false;
    let didPublish = false;
    try {
      let isApplied = await setLocalTrackEnabled({
        channelId,
        trackType,
        isEnabled,
      });
      if (!guard.isCurrent()) return dispatch({ type: '' });
      if (!isApplied && isEnabled) {
        let track;
        try {
          track = await ensureLocalTrack({ channelId, trackType });
        } catch (error) {
          guard.setEntry(
            error?.viewerCameraTrackEntry ?? getLocalTrackEntry({ channelId })
          );
          if (!guard.isCurrent()) return dispatch({ type: '' });
          trackViewerCameraEvent({
            type: EventTypes.LIVESTREAM_VIEWER_CAMERA_PREVIEW_FAILED,
            streamId,
            payload: {
              trigger: PREVIEW_TRIGGER.TOGGLE,
              trackType,
              isPartial: hasAnyLocalTrack({ channelId }),
              ...getCameraErrorData(error),
              step: error?.viewerCameraStep ?? LOCAL_TRACK_STEP.GET_USER_MEDIA,
            },
          });
          throw error;
        }
        guard.refreshEntry();
        if (!track || !guard.isCurrent()) return dispatch({ type: '' });
        isApplied = !!track;
        didCreateTrack = isApplied;
      }
      if (!isApplied) {
        return dispatch({ type: '' });
      }
      // Publish whenever performing, even if no publish is active: an earlier
      // publish may never have succeeded (e.g. the retry modal was dismissed),
      // and concurrent publishes are already serialized in the resource.
      const latestStatus = getExclusivePrivateStatus(getState(), streamId);
      if (isEnabled && latestStatus === ExclusivePrivateStatus.PERFORMING) {
        const publishedTracks = await publishLocalTracks({
          channelId,
          localTracks: getLocalTracks({ channelId }),
        });
        if (!publishedTracks) {
          return dispatch({ type: '' });
        }
        if (
          !guard.isCurrent({
            requiredStatus: ExclusivePrivateStatus.PERFORMING,
          })
        ) {
          return dispatch({ type: '' });
        }
        didPublish = true;
      }
    } catch (error) {
      if (!guard.isCurrent()) return dispatch({ type: '' });
      if (isEnabled) {
        await setLocalTrackEnabled({
          channelId,
          trackType,
          isEnabled: false,
        }).catch(() => false);
      }
      if (!guard.isCurrent()) return dispatch({ type: '' });
      // A denied device gets the permission guide; other failures keep the
      // generic toast.
      if (getIsPermissionDeniedError(error)) {
        return dispatch(
          openCameraMicrophoneAsker({ mode: TRACK_PERMISSION_MODE[trackType] })
        );
      }
      dispatch(pushToastr(SET_UP_FAILED_TOAST));
      return dispatch({ type: '' });
    }
    const shouldMoveToMiniWindow =
      trackType === LOCAL_TRACK_TYPE.VIDEO && !isEnabled;
    const createdVideoData =
      didCreateTrack && trackType === LOCAL_TRACK_TYPE.VIDEO
        ? {
            canSwitchCamera: await getHasMultipleCameras(),
          }
        : {};
    if (!guard.isCurrent()) return dispatch({ type: '' });
    return dispatch(
      mergeViewerCameraData({
        streamId,
        data: {
          [TRACK_ENABLED_DATA_KEY[trackType]]: isEnabled,
          ...(shouldMoveToMiniWindow && { isInMainWindow: false }),
          ...(didPublish && { isPublishing: true }),
          ...createdVideoData,
        },
      })
    );
  };

/**
 * Set whether the viewer camera is displayed in the main window.
 * @kind action
 * @param {object} params
 * @param {string} params.streamId - Livestream id.
 * @param {boolean} params.isInMainWindow - Whether to use the main window.
 * @returns {Function} Redux thunk action.
 * @example
 * dispatch(setViewerCameraInMainWindow({
 *   streamId: 'stream-id',
 *   isInMainWindow: true,
 * }));
 */
const setViewerCameraInMainWindow =
  ({ streamId, isInMainWindow }) =>
  dispatch =>
    dispatch(
      mergeViewerCameraData({
        streamId,
        data: { isInMainWindow: !!isInMainWindow },
      })
    );

/**
 * Switch the viewer camera between available devices.
 * @kind action
 * @param {object} params
 * @param {string} params.streamId - Livestream id.
 * @returns {Function} Redux thunk action.
 * @example
 * dispatch(switchViewerCamera({ streamId: 'stream-id' }));
 */
const switchViewerCamera =
  ({ streamId }) =>
  async (dispatch, getState) => {
    const { channelId, isSessionActive } = getViewerCameraSessionContext({
      state: getState(),
      streamId,
    });
    if (!isSessionActive) {
      return dispatch({ type: '' });
    }
    const guard = createViewerCameraEntryGuard({
      getState,
      streamId,
      channelId,
      entry: getLocalTrackEntry({ channelId }),
    });
    const { switched, reason } = await switchLocalCamera({ channelId });
    if (!guard.isCurrent()) {
      return dispatch({ type: '' });
    }
    if (switched) {
      const currentVersion = getViewerCameraData(
        getState(),
        streamId,
        'cameraSwitchVersion'
      );
      return dispatch(
        mergeViewerCameraData({
          streamId,
          data: { cameraSwitchVersion: currentVersion + 1 },
        })
      );
    }
    if (reason === SWITCH_CAMERA_REASON.SINGLE_CAMERA) {
      return dispatch(
        mergeViewerCameraData({
          streamId,
          data: { canSwitchCamera: false },
        })
      );
    }
    return dispatch(pushToastr(SET_UP_FAILED_TOAST));
  };

export {
  PREVIEW_TRIGGER,
  startViewerCameraPreview,
  publishViewerCamera,
  startViewerCamera,
  stopViewerCamera,
  setViewerCameraTrackEnabled,
  setViewerCameraInMainWindow,
  switchViewerCamera,
  invalidateViewerCameraPreview,
  openViewerCameraStartModal,
  openViewerCameraMoreModal,
};
